import {
  callGemini,
  parseJSON,
  sanitizeStringArray,
  JSON_RULES,
  LANGUAGE_RULE,
} from "../../onboarding/OnboardingAPI";
import {
  getCommunityMediaConfig,
  buildVoiceOfCustomerPromptBlock,
  CommunityMediaConfig,
} from "../../../../database/community/mediaConfig";
import { Community } from "../../../../types/Community";
import {
  SubscriptionLandingInfo,
  SubscriptionPlan,
  FAQ,
} from "../../../../types/Paywall";

/**
 * The marketing copy a paywall generation produces. Product identifiers
 * (RevenueCat ids, prices) are intentionally NOT generated — the AI only
 * writes copy. Plans come back as copy-only and are merged onto any existing
 * plans by the caller so RevenueCat wiring is preserved.
 */
export type GeneratedPaywallDraft = {
  title: string;
  subtitle: string;
  buttonText: string;
  featuresTitle: string;
  testimonialsTitle: string;
  features: string[];
  faqs: FAQ[];
  motivationalPrompt?: string;
  /** Copy-only plan suggestions: id, price, and products are left empty. */
  plans: SubscriptionPlan[];
};

const formatValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    const items = value
      .map((item) => (typeof item === "string" ? item.trim() : `${item ?? ""}`.trim()))
      .filter(Boolean);
    return items.length ? items.join(", ") : "Not provided";
  }
  if (typeof value === "string") return value.trim() || "Not provided";
  return "Not provided";
};

const sanitizeFaqs = (value: unknown): FAQ[] =>
  Array.isArray(value)
    ? value
        .map((entry) => ({
          question:
            entry && typeof entry.question === "string" ? entry.question.trim() : "",
          answer: entry && typeof entry.answer === "string" ? entry.answer.trim() : "",
        }))
        .filter((faq) => faq.question && faq.answer)
    : [];

const sanitizePlans = (value: unknown): SubscriptionPlan[] =>
  Array.isArray(value)
    ? value
        .map((entry) => ({
          // Identifiers and pricing stay empty — the creator wires those to
          // RevenueCat. We only fill generated copy here.
          id: "",
          price: "",
          products: [],
          title: entry && typeof entry.title === "string" ? entry.title.trim() : "",
          subtitle:
            entry && typeof entry.subtitle === "string" ? entry.subtitle.trim() : "",
          description:
            entry && typeof entry.description === "string"
              ? entry.description.trim()
              : "",
          ...(entry && typeof entry.type === "string" && entry.type.trim()
            ? { type: entry.type.trim() }
            : {}),
        }))
        .filter((plan) => plan.title)
    : [];

const buildContextBlock = (
  community: Partial<Community> | null | undefined,
  mediaConfig: CommunityMediaConfig | null | undefined
): string => {
  const persona = mediaConfig?.buyerPersona?.trim();
  const personaNote = persona
    ? `\nTarget audience (buyer persona): ${persona}. Write every line as if speaking directly to this person — their fears, frustrations, and desires.`
    : "";

  const vocBlock = buildVoiceOfCustomerPromptBlock(mediaConfig?.voiceOfCustomer);
  const vocNote = vocBlock
    ? `\n\n${vocBlock}\n\nReuse this exact audience phrasing in the headline, features, and CTA where it fits.`
    : "";

  return `Community context:
- Community name: ${formatValue(community?.title)}
- Description: ${formatValue(community?.description)}
- Short description: ${formatValue(community?.shortDescription)}
- Category: ${formatValue(community?.categoryName)}
- Tags: ${formatValue(community?.tags)}
- Author: ${formatValue(community?.author?.fullName)}
- Author bio: ${formatValue(community?.author?.description)}
- Writing style: ${formatValue(mediaConfig?.writingStyle ?? community?.writingStyle)}
- Visual style: ${formatValue(mediaConfig?.visualStyle ?? community?.visualStyle)}${personaNote}${vocNote}`;
};

/**
 * Generate paywall marketing copy from a community's information.
 *
 * Reads the community doc plus its media config (writing style, visual style,
 * buyer persona, voice of customer) and produces a ready-to-review paywall
 * draft. Pricing and product identifiers are never generated.
 */
export async function generatePaywallFromCommunity({
  community,
  communityId,
  mediaConfig,
}: {
  community?: Partial<Community> | null;
  communityId?: string;
  mediaConfig?: CommunityMediaConfig | null;
}): Promise<GeneratedPaywallDraft> {
  let resolvedMediaConfig = mediaConfig ?? null;
  if (!resolvedMediaConfig && communityId) {
    try {
      resolvedMediaConfig = await getCommunityMediaConfig(communityId, {
        communityData: community ?? undefined,
      });
    } catch (error) {
      // Media config is optional context — fall back to the root community doc.
      console.warn("Could not load community media config for paywall generation", error);
    }
  }

  const contextBlock = buildContextBlock(community, resolvedMediaConfig);

  const instruction = `
You are a conversion copywriter building the subscription paywall (landing page) for a community app.

${contextBlock}

Write persuasive, specific copy that sells this community's membership. Speak to the audience's transformation, not generic features. Keep it concrete and benefit-led.

Return ONLY valid JSON with this exact structure:
{
  "title": "Hero headline — the core promise, under 12 words",
  "subtitle": "One supporting sentence that expands the promise",
  "buttonText": "Short CTA button label, 2-4 words (e.g. Start now, Join today)",
  "featuresTitle": "Section title that introduces the features list",
  "testimonialsTitle": "Section title that introduces member testimonials",
  "features": ["5-7 benefit-led bullets describing what members get and the outcome"],
  "faqs": [{ "question": "Common objection or question", "answer": "Reassuring, specific answer" }],
  "motivationalPrompt": "One short motivational line shown to hesitant users",
  "plans": [
    { "title": "Plan name", "subtitle": "Short value line", "description": "1-2 sentences on what's included", "type": "Optional badge like Popular or Best Value" }
  ]
}

Generate 5-7 features, 4-6 faqs, and 2-3 plans. Do NOT invent prices, currencies, or product identifiers — only write plan copy.
${LANGUAGE_RULE}
${JSON_RULES}`;

  const text = await callGemini([{ text: instruction }], 4000, false);
  const raw = parseJSON<Partial<GeneratedPaywallDraft>>(text);

  return {
    title: typeof raw.title === "string" ? raw.title.trim() : "",
    subtitle: typeof raw.subtitle === "string" ? raw.subtitle.trim() : "",
    buttonText: typeof raw.buttonText === "string" ? raw.buttonText.trim() : "",
    featuresTitle: typeof raw.featuresTitle === "string" ? raw.featuresTitle.trim() : "",
    testimonialsTitle:
      typeof raw.testimonialsTitle === "string" ? raw.testimonialsTitle.trim() : "",
    features: sanitizeStringArray(raw.features),
    faqs: sanitizeFaqs(raw.faqs),
    motivationalPrompt:
      typeof raw.motivationalPrompt === "string" ? raw.motivationalPrompt.trim() : "",
    plans: sanitizePlans(raw.plans),
  };
}

/**
 * Merge generated plan copy onto existing plans by index so RevenueCat
 * identifiers (id, price, products) on already-configured plans survive.
 * Extra generated plans are appended as drafts with empty identifiers.
 */
export const mergeGeneratedPlans = (
  existing: SubscriptionPlan[],
  generated: SubscriptionPlan[]
): SubscriptionPlan[] => {
  if (!generated.length) return existing;

  const merged = generated.map((draft, index) => {
    const current = existing[index];
    if (!current) return draft;
    return {
      ...current,
      title: draft.title || current.title,
      subtitle: draft.subtitle || current.subtitle,
      description: draft.description || current.description,
      ...(draft.type ? { type: draft.type } : {}),
    };
  });

  // Keep any existing plans beyond what was generated rather than dropping them.
  if (existing.length > generated.length) {
    merged.push(...existing.slice(generated.length));
  }

  return merged;
};
